import { notification } from 'antd';
import { useState } from 'react';
import type { UseFormTrigger } from 'react-hook-form';

import { onlyDigits } from '@/services/formatter';
import { useAppDispatch, useAppSelector } from '@/store';
import { createNewOrder, setLoanData } from '@/store/newOrder.slice';
import type { TNewOrderDto } from '@/types/newOrder';

import { getAutoCreditDraftKey } from '../../helpers/helpers';
import useAutoCredRequestId from '../../hooks/useAutoCredRequestId';
import { REQUIRED_FIELDS } from '../constants';
import type { CreateAutoCreditProps } from '../types/form-types';

interface IProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  trigger: UseFormTrigger<CreateAutoCreditProps>;
  createRequest: (requestId: string) => void;
}

const useCreateAutoCredit = ({
  currentStep,
  setCurrentStep,
  trigger,
  createRequest
}: IProps) => {
  const dispatch = useAppDispatch();

  const { otpCode } = useAppSelector((state) => state.orderStep);
  const { orderApp, clientInfo } = useAppSelector((state) => state.newOrder);
  const { dealer } = useAppSelector((state) => state.userInfo);

  const { requestId } = useAutoCredRequestId();

  const [createLoading, setCreateLoading] = useState(false);

  const onSubmit = async (values: CreateAutoCreditProps) => {
    const isValid = await trigger(REQUIRED_FIELDS[currentStep]);
    if (!isValid) return;

    // TODO Отключено до настройки ПКБ
    // if (currentStep === 0) {
    //   await getClientInfo();
    // }

    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
      return;
    }

    setCreateLoading(true);

    const product = {
      dealer: Number(dealer?.code),
      chosenDealer: Number(values.dealer),
      autoType: Number(values.autoType),
      markModelYear: values.markModelYear?.brand
        ? {
            model: Number(values.markModelYear.model?.code),
            brand: Number(values.markModelYear.brand?.code),
            equipment: values.markModelYear.equipment?.uuid || '',
            year:
              values.markModelYear.year || new Date().getFullYear().toString()
          }
        : null,
      usedCar: values.usedCar?.info
        ? {
            uuid: values.usedCar.info?.uuid || '',
            vinCode: values.usedCar.vinCode || '',
            year: values.usedCar.year || '',
            color: values.usedCar.color || '',
            engineCapacity: String(values.usedCar.engineCapacity || ''),
            enginePower: String(values.usedCar.enginePower || '')
          }
        : null,
      carYear:
        values?.markModelYear?.year?.toString() ||
        values.usedCar?.year ||
        new Date().getFullYear().toString(),
      costCar: values.costCar.replace(/\s/g, ''),
      requestAmount: values.costCar.replace(/\s/g, ''),
      initFee: values.initialPayment.replace(/\s/g, ''),
      loanTerm: Number(values.loanTerm),
      engineType: Number(values.engineType),
      paymentScheme: Number(values.paymentScheme),
      proofInCome: values.proofInCome,
      eurasianTariffType: values.eurasianTariffType,
      stocksBonds: values.stockBounds.map((item) => ({
        id: item.uuid,
        value: item.bankUuid,
        label: item.name,
        checked: item.checked,
        info: item.description,
        code: item.code,
        bank_name: item.bank,
        loan_period: item.loanPeriod || String(item.grace),
        grace: item.grace
      }))
    };

    const personalInfo = {
      docType: Number(values.documentType),
      sex: Number(values.gender),
      jobAddress: values.jobAddress,
      dateBirth: values.birthDate,
      firstName: clientInfo.firstName,
      middleName: clientInfo.middleName,
      lastName: clientInfo.lastName,
      clientIin: clientInfo.iin,
      familyStatus: Number(values.marriageStatus),
      phoneNumber: onlyDigits(clientInfo?.telNum),
      spouseInfo: null
    };
    const files = {
      noticeActivity: values.noticeActivity,
      docScanSocialStatus: values.docScanSocialStatus,
      clientPhoto: values.clientPhoto,
      docPhoto: values.docPhoto,
      incomeCert: values.incomeCert || {
        name: 'blank',
        base64: 'blank',
        size: 0
      },
      otherDoc: values.otherDoc
    };

    const additionalInfo: TNewOrderDto['additionalInfo'] = {
      files: files,
      socialStatus: Number(values.socialStatus),
      addContactsFullName: values.contactName,
      additionalSalary: values.additionalSalary.replace(/\s/g, ''),
      salary: values.salary.replace(/\s/g, ''),
      addContactsTelNum: onlyDigits(values.contactPhone),
      addContactsIsRelated: Number(values.contactType),
      children: values.childrenCount,
      companyName: values.companyName,
      jobTitle: values.jobTitle,
      companyType: Number(values.companyType),
      workTelNumber: onlyDigits(values.workPhoneNumber)
    };
    if (values.isIndividualEntrepreneur !== undefined)
      additionalInfo.isOwner = values.isIndividualEntrepreneur;

    const newOrderDto: TNewOrderDto = {
      uuid: orderApp?.uuid || requestId,
      status: 'draft',
      otp: otpCode || orderApp?.otp || '',
      otpDate: new Date().toISOString(),
      sendTo: values.sentTo || 'banks_leasing',
      code: clientInfo.id.toString(),
      productInfo: {
        product,
        additionalProducts: values.additionalProducts
          .filter((item) => !item.hidden && !item.hiddenByAutoType)
          .map((item) => ({
            id: item.id,
            value: item.value,
            label: item.label,
            checked: item.checked,
            selected_insurance_uuid: item.selectedInsuranceUuid,
            insurance_uuid: item.insuranceUuid,
            info: item.info,
            loan_period: item.loanPeriod,
            code: item.code,
            bank_name: item.bankName,
            grace: item.grace,
            partner_code: item.partnerCode,
            product_code: item.productCode
          }))
      },
      personalInfo,
      additionalInfo
    };

    const response = await dispatch(createNewOrder(newOrderDto));

    if (createNewOrder.rejected.match(response)) {
      setCreateLoading(false);
      notification.error({
        title: response.error.message
      });
      return;
    }

    if (response.payload) {
      const equipment = values.markModelYear?.equipment;
      const carInfo = {
        brand:
          values.markModelYear?.brand?.name ||
          values.usedCar?.info?.brand_name ||
          '',
        model:
          values.markModelYear?.model?.name ||
          values.usedCar?.info?.model_name ||
          '',
        year: values.markModelYear?.year || values.usedCar?.info?.year || ''
      };
      dispatch(
        setLoanData({
          amount: values.costCar.replace(/\s/g, ''),
          auto: `${carInfo.brand || ''} ${
            carInfo.model || ''
          }${equipment ? ` ${equipment.name}` : ''}, ${carInfo.year}`
        })
      );

      // Чистим сохраненные данные формы
      sessionStorage.removeItem(getAutoCreditDraftKey(newOrderDto.uuid));
      setCreateLoading(false);
      if (response.payload.requestId) createRequest(response.payload.requestId);
    }
  };

  return { createLoading, onSubmit };
};

export default useCreateAutoCredit;
